import gql from 'graphql-tag';
import { PimcoreEntityType } from '@/types/entity';
import {
  EntityCrudClient,
  EntityCreateParams,
} from '@/types/EntityCrudClient';
import { DocumentNode } from 'graphql/language/ast';

export default class EntityCrud {
  entity: string;

  client: EntityCrudClient;

  constructor(entity: string, client: EntityCrudClient) {
    this.entity = entity;
    this.client = client;
  }

  async fetch(id: number | string, fields: PimcoreEntityType[] | string[]) {
    const queryName = `get${this.entity}`;
    const query = gql`query getEntity($id: Int!) {
      ${queryName} (id: $id) {
        ${fields.join(' ')}
      }
    }`;

    const variables = {
      id: Number(id),
    };

    const result = await this.client.query({
      query,
      variables,
    });

    const unpackedData = { ...result.data[queryName] };
    delete unpackedData.__typename;

    return unpackedData;
  }

  async update(id: number | string, input: Record<string, any>) {
    const mutationName = `update${this.entity}`;
    const inputType = `Update${this.entity}Input`;

    const mutation = gql`mutation UpdateEntity($id: Int!, $input: ${inputType}) {
      ${mutationName} (id: $id, input: $input) {
        message
        success
      }
    }`;

    const variables = {
      id: Number(id),
      input,
    };

    return this.mutate(mutation, variables);
  }

  async create({
    input,
    key = `(${Date.now()})`,
    folderId,
    folderPath,
  }: EntityCreateParams) {
    const mutationName = `create${this.entity}`;
    const inputType = `Update${this.entity}Input`;

    const mutation = gql`
      mutation createEntity($key: String!, $input: ${inputType}, $path: String $parentId: Int) {
        ${mutationName} (key: $key, input: $input, parentId: $parentId, path: $path, published: true) {
          success
          message
        }
      }
    `;

    const variables: Record<string, any> = {
      key,
      input,
    };

    if (folderId) {
      variables.parentId = folderId;
    } else if (folderPath) {
      variables.path = folderPath;
    }

    return this.mutate(mutation, variables);
  }

  async delete(id: number | string) {
    const mutationName = `delete${this.entity}`;
    const variables = {
      id: Number(id),
    };

    const mutation = gql`mutation deleteEntity($id: Int!) {
      ${mutationName} (id: $id) {
        success
        message
      }
    }`;

    return this.mutate(mutation, variables);
  }

  private async mutate(
    mutation: DocumentNode,
    variables: Record<string, any>,
    client = 'default',
  ) {
    const result = await this.client.mutate({
      mutation,
      variables,
      client,
    });

    return Object.values(result.data)[0];
  }
}
